import { LawsuitAppealIncident } from "@customTypes/responses/lawsuit";
import React from "react";
import { WithPos } from "@customTypes/generics";
import uuid from "react-uuid";
import { OrganizedMovements } from "@customTypes/pages/lawsuit";

const useAppealIncidentsTree = (
  movement: WithPos<LawsuitAppealIncident>,
  organizedMovements: OrganizedMovements,
  options?: Record<string, unknown>
) => {
  const { htmlTag, customClass, extraSpace, lineBlockPx, proportion } = {
    htmlTag: "div",
    customClass: "",
    extraSpace: true,
    lineBlockPx: "",
    proportion: "Half",
    ...options,
  };

  const { x, y, z } = movement.pos;
  const { caseType } = movement;

  const { appeals, incidents } = organizedMovements;

  const getLevelParent = (
    incident: WithPos<LawsuitAppealIncident>,
    index: number
  ): WithPos<LawsuitAppealIncident> => {
    const parentIncident = incidents.find(
      (possibleParent) => possibleParent.id === incident.parentCaseId
    );
    if (parentIncident.pos.x !== index + 1) return getLevelParent(parentIncident, index);
    return parentIncident;
  };

  const checkHigherSibling = (
    incident: WithPos<LawsuitAppealIncident>,
    levelParent: WithPos<LawsuitAppealIncident>
  ) =>
    incident.pos.x === levelParent.pos.x &&
    incident.pos.z === z &&
    incident.pos.y > levelParent.pos.y;

  const getLineType = (index: number, arrayLength: number) => {
    switch (index) {
      case x: {
        if (incidents.find((thisIncident) => thisIncident.parentCaseId === movement.id)) {
          return ` rst__lineFullVertical${proportion}`;
        }
        return "";
      }
      case x - 1: {
        if (
          incidents.find(
            (incident) => incident.pos.x === x && incident.pos.y > y && incident.pos.z === z
          ) ||
          (appeals.find((appeal) => appeal.pos.z > z) && x === 1)
        )
          return ` rst__lineFullVertical${proportion} ${
            arrayLength === x ? ` rst__line${proportion}HorizontalRight` : ""
          }`;
        return arrayLength === x
          ? ` rst__line${proportion}VerticalTop rst__line${proportion}HorizontalRight`
          : "";
      }
      case 0: {
        const levelParent = getLevelParent(movement, index);
        if (
          appeals.find((appeal) => appeal.pos.z > z) ||
          incidents.find((incident) => checkHigherSibling(incident, levelParent))
        )
          return ` rst__lineFullVertical${proportion} ${
            x === 1 ? ` rst__line${proportion}HorizontalRight` : ""
          }`;
        return "";
      }
      default: {
        const levelParent = getLevelParent(movement, index);
        return incidents.find((incident) => checkHigherSibling(incident, levelParent))
          ? ` rst__lineFullVertical${proportion}`
          : "";
      }
    }
  };

  const spaceBeforeCard = new Array(x).fill(null).map((element, index) =>
    React.createElement(htmlTag, {
      key: uuid(),
      className: ` rst__lineBlock${lineBlockPx}${getLineType(index, x)}${customClass}`,
    })
  );

  const spaceBetweenCards =
    caseType === "CTE_APPEAL"
      ? React.createElement(htmlTag, {
          key: uuid(),
          className: `rst__lineBlock${lineBlockPx}${
            appeals.find((appeal) => appeal.pos.z > z) ||
            incidents.find((incident) => incident.parentCaseId === movement.id)
              ? ` rst__lineFullVertical${proportion}`
              : ""
          }${customClass}`,
        })
      : new Array(extraSpace ? x + 1 - z : x).fill(null).map((element, index) =>
          React.createElement(htmlTag, {
            key: uuid(),
            className: ` rst__lineBlock${lineBlockPx}${getLineType(index, x + 1)}${customClass}`,
          })
        );

  return {
    spaceBeforeCard,
    spaceBetweenCards,
  };
};

export default useAppealIncidentsTree;
